import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useMenuIcon, useMenuName } from '@/hooks/useMenuIcon';
import { PageContainer, PageHeader } from '@/components/layout';
import { PageTitle } from '@/components/common/PageTitle';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { AlertCircle, PenSquare, ChevronLeft, ChevronRight } from 'lucide-react';
import { inquiryApi, InquiryListItem, InquiryStatus } from '@/api/inquiryApi';
import { LoadingBlock } from '@/components/ui/spinner';

const STATUS_LABEL: Record<InquiryStatus, string> = {
  PENDING: '답변대기',
  ANSWERED: '답변완료',
  CLOSED: '완료',
};


const STATUS_CLASS: Record<InquiryStatus, string> = {
  PENDING: 'bg-yellow-100 text-yellow-700 border-yellow-300',
  ANSWERED: 'bg-green-100 text-green-700 border-green-300',
  CLOSED: 'bg-gray-100 text-gray-500 border-gray-300',
};

export default function InquiryListPage() {
  const navigate = useNavigate();
  const menuIcon = useMenuIcon();
  const menuName = useMenuName();

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [items, setItems] = useState<InquiryListItem[]>([]);
  const [currentPage, setCurrentPage] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [totalElements, setTotalElements] = useState(0);

  const PAGE_SIZE = 10;

  const loadList = (page: number) => {
    setLoading(true);
    setError(null);
    inquiryApi.getMyInquiries(page, PAGE_SIZE)
      .then(res => {
        setItems(res.data.content ?? []);
        setTotalPages(res.data.totalPages ?? 0);
        setTotalElements(res.data.totalElements ?? 0);
        setCurrentPage(res.data.number ?? page);
      })
      .catch(() => setError('문의 목록을 불러오지 못했습니다.'))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    loadList(0);
  }, []);

  const formatDate = (iso: string) =>
    iso ? new Date(iso).toLocaleDateString('ko-KR') : '-';

  return (
    <>
      <PageTitle title="문의 내역" />
      <PageContainer maxWidth="full">
      <PageHeader 
        title={menuName || '1:1 문의'}
        description="문의사항을 남겨주시면 빠르게 답변드리겠습니다."
        icon={menuIcon}
        action={
          <Button onClick={() => navigate('/dashboard/inquiry/new')}>
            <PenSquare className="w-4 h-4 mr-2" />
            문의하기
          </Button>
        }
      />

      {error && (
        <Alert variant="destructive">
          <AlertCircle className="w-4 h-4" />
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}

      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base font-medium text-gray-700">
            총 {totalElements.toLocaleString()}건
          </CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          {loading ? (
            <LoadingBlock size="xl" py={20} />
          ) : (
            <div className="overflow-x-auto">
              <Table>
              <TableHeader>
                <TableRow className="bg-gray-50">
                  <TableHead className="w-16 text-center whitespace-nowrap">번호</TableHead>
                  <TableHead>제목</TableHead>
                  <TableHead className="w-24 text-center">상태</TableHead>
                  <TableHead className="w-28 text-center">등록일</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {items.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={4} className="text-center text-gray-400 py-16">
                      <PenSquare className="w-12 h-12 mx-auto mb-3 opacity-30" />
                      <p>등록된 문의가 없습니다.</p>
                      <Button variant="outline" className="mt-4" onClick={() => navigate('/dashboard/inquiry/new')}>
                        첫 번째 문의 작성하기
                      </Button>
                    </TableCell>
                  </TableRow>
                ) : (
                  items.map((item, idx) => (
                  <TableRow
                    key={item.id}
                    className="cursor-pointer hover:bg-blue-50/50 transition-colors"
                    onClick={() => navigate(`/dashboard/inquiry/${item.id}`)}
                  >
                    <TableCell className="text-center text-gray-400 text-sm">
                      {totalElements - (currentPage * PAGE_SIZE + idx)}
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-2">
                        {item.isSecret && (
                          <span className="text-xs text-gray-400" title="비밀글">🔒</span>
                        )}
                        <span className="font-medium text-gray-800 hover:text-primary line-clamp-1">
                          {item.title}
                        </span>
                        {item.status === 'ANSWERED' && !item.isReadByUser && (
                          <Badge className="bg-blue-500 text-white text-xs px-1.5 py-0 h-5">NEW</Badge>
                        )}
                      </div>
                    </TableCell>
                    <TableCell className="text-center">
                      <Badge className={STATUS_CLASS[item.status]}>
                        {STATUS_LABEL[item.status]}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-center text-sm text-gray-500">
                      {formatDate(item.createdAt)}
                    </TableCell>
                  </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* 페이지네이션 */}
      {totalPages > 1 && (
        <div className="flex justify-center items-center gap-1">
          <Button
            variant="outline" size="sm"
            disabled={currentPage === 0}
            onClick={() => loadList(currentPage - 1)}
          >
            <ChevronLeft className="w-4 h-4" />
          </Button>
          {Array.from({ length: totalPages }, (_, i) => (
            <Button
              key={i}
              variant={i === currentPage ? 'default' : 'outline'}
              size="sm"
              className="w-9"
              onClick={() => loadList(i)}
            >
              {i + 1}
            </Button>
          ))}
          <Button
            variant="outline" size="sm"
            disabled={currentPage === totalPages - 1}
            onClick={() => loadList(currentPage + 1)}
          >
            <ChevronRight className="w-4 h-4" />
          </Button>
        </div>
      )}
    </PageContainer>
    </>
  );
}
